import React, { useEffect, useState } from "react";
import {
  FaEdit,
  FaEye,
  FaFilter,
  FaMoneyBillWave,
  FaPlus,
  FaTrash,
  FaChevronRight,
  FaChevronDown,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import CompactActionBar from "../components/CompactActionBar";
import ConfirmDialog from "../components/ConfirmDialog";
import { PermissionGuard } from "../components/PermissionGuard";
import SharedFilter from "../components/SharedFilter";
import { useAuth } from "../contexts/AuthContext";
import "../css/pages/Income.css";
import {
  Income,
  IncomeFilters,
  incomeService,
} from "../services/incomeService";
import { projectService } from "../services/projectService";
import { unitService } from "../services/unitService";

interface Project {
  id: number;
  name: string;
}

const IncomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [incomes, setIncomes] = useState<Income[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [wings, setWings] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showChequeTrackingModal, setShowChequeTrackingModal] = useState(false);
  const [editingIncome, setEditingIncome] = useState<Income | null>(null);
  const [selectedBookingIncomes, setSelectedBookingIncomes] = useState<
    Income[]
  >([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [incomeToDelete, setIncomeToDelete] = useState<Income | null>(null);

  // Cheque tracking states
  const [missedCheques, setMissedCheques] = useState<Income[]>([]);
  const [upcomingCheques, setUpcomingCheques] = useState<Income[]>([]);
  const [activeTab, setActiveTab] = useState<"missed" | "upcoming">("missed");
  const [depositDates, setDepositDates] = useState<{ [key: number]: string }>(
    {}
  );
  const [updatingDeposit, setUpdatingDeposit] = useState<{
    [key: number]: boolean;
  }>({});
  // Cheque tracking: pagination + filters
  const CHEQUE_PAGE_SIZE = 20;
  const [missedPage, setMissedPage] = useState(1);
  const [upcomingPage, setUpcomingPage] = useState(1);
  const [missedTotal, setMissedTotal] = useState(0);
  const [upcomingTotal, setUpcomingTotal] = useState(0);
  const [missedTotalPages, setMissedTotalPages] = useState(1);
  const [upcomingTotalPages, setUpcomingTotalPages] = useState(1);
  const [chequeProjectFilter, setChequeProjectFilter] = useState("");
  const [upcomingShowAll, setUpcomingShowAll] = useState(false);
  // Mark-received action state (missed tab)
  const [receivedDates, setReceivedDates] = useState<{ [key: number]: string }>(
    {}
  );
  const [markingReceived, setMarkingReceived] = useState<{
    [key: number]: boolean;
  }>({});

  // Expandable return allocations
  const [expandedReturnIds, setExpandedReturnIds] = useState<Set<number>>(new Set());

  const toggleExpandReturn = (incomeId: number) => {
    setExpandedReturnIds(prev => {
      const next = new Set(prev);
      if (next.has(incomeId)) {
        next.delete(incomeId);
      } else {
        next.add(incomeId);
      }
      return next;
    });
  };

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(50);

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filterProject, setFilterProject] = useState("");
  const [filterWing, setFilterWing] = useState("");
  const [filterUnit, setFilterUnit] = useState("");
  const [filterCustomer, setFilterCustomer] = useState("");
  const [filterMobile, setFilterMobile] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    project: "",
    wing: "",
    unit: "",
    customer: "",
    mobile: "",
    dateFrom: "",
    dateTo: "",
  });
  
  const [statusFilter, setStatusFilter] = useState("");
  const [paymentMethodFilter, setPaymentMethodFilter] = useState("");

  // Form data
  const [formData, setFormData] = useState({
    bookingId: "",
    amount: "",
    paymentDate: "",
    paymentMethod: "",
    referenceNumber: "",
    bankName: "",
    description: "",
    receiptName: "",
  });

  const handleApplyFilters = () => {
    setAppliedFilters({
      project: filterProject,
      wing: filterWing,
      unit: filterUnit,
      customer: filterCustomer,
      mobile: filterMobile,
      dateFrom: filterDateFrom,
      dateTo: filterDateTo,
    });
    setCurrentPage(1);
    setShowFilters(false);
  };

  const clearAllFilters = () => {
    setFilterProject("");
    setFilterWing("");
    setFilterUnit("");
    setFilterCustomer("");
    setFilterMobile("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setAppliedFilters({
      project: "",
      wing: "",
      unit: "",
      customer: "",
      mobile: "",
      dateFrom: "",
      dateTo: "",
    });
    setCurrentPage(1);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const filters: IncomeFilters = {
          page: currentPage,
          limit,
          ...(appliedFilters.customer && { search: appliedFilters.customer }),
          ...(appliedFilters.mobile && { search: appliedFilters.mobile }),
          ...(appliedFilters.unit && { search: appliedFilters.unit }),
          ...(statusFilter && { status: statusFilter }),
          ...(appliedFilters.project && { project: appliedFilters.project }),
          ...(paymentMethodFilter && {
            paymentMethod: paymentMethodFilter,
          }),
          ...(appliedFilters.dateFrom && { dateFrom: appliedFilters.dateFrom }),
          ...(appliedFilters.dateTo && { dateTo: appliedFilters.dateTo }),
        };

        const response = await incomeService.getIncomes(filters);
        setIncomes(response.incomes);
        setTotal(response.pagination.total);
        setTotalPages(response.pagination.totalPages);
      } catch (error) {
        console.error("Error fetching incomes:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    currentPage,
    limit,
    statusFilter,
    paymentMethodFilter,
    appliedFilters,
  ]);

  // Initial load effect for projects
  useEffect(() => {
    fetchProjects();
  }, []);

  // Fetch wings when project changes
  useEffect(() => {
    const fetchWings = async () => {
      if (filterProject) {
        try {
          const projectWings = await unitService.getWingsByProject(parseInt(filterProject));
          setWings(projectWings);
        } catch (error) {
          console.error('Error fetching wings:', error);
          setWings([]);
        }
      } else {
        setWings([]);
        setFilterWing(''); // Clear wing when no project selected
      }
    };
    fetchWings();
  }, [filterProject]);

  const fetchIncomes = async () => {
    try {
      setLoading(true);
      const filters: IncomeFilters = {
        page: currentPage,
        limit,
        ...(appliedFilters.customer && { search: appliedFilters.customer }),
        ...(appliedFilters.mobile && { search: appliedFilters.mobile }),
        ...(appliedFilters.unit && { search: appliedFilters.unit }),
        ...(statusFilter && { status: statusFilter }),
        ...(appliedFilters.project && { project: appliedFilters.project }),
        ...(paymentMethodFilter && { paymentMethod: paymentMethodFilter }),
        ...(appliedFilters.dateFrom && { dateFrom: appliedFilters.dateFrom }),
        ...(appliedFilters.dateTo && { dateTo: appliedFilters.dateTo }),
      };

      const response = await incomeService.getIncomes(filters);
      setIncomes(response.incomes);
      setTotal(response.pagination.total);
      setTotalPages(response.pagination.totalPages);
    } catch (error) {
      console.error("Error fetching incomes:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await projectService.getProjects();
      // Response is an array, not an object with projects property
      setProjects(response);
      
      // Auto-select first project
      if (response.length > 0) {
        const firstProjectId = response[0].id.toString();
        setFilterProject(firstProjectId);
        setAppliedFilters(prev => ({ ...prev, project: firstProjectId }));
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  const fetchChequeTrackingData = async () => {
    try {
      const [missedResponse, upcomingResponse] = await Promise.all([
        incomeService.getMissedChequeDeposits({
          page: missedPage,
          limit: CHEQUE_PAGE_SIZE,
          project: chequeProjectFilter || undefined,
        }),
        incomeService.getUpcomingChequeDeposits({
          page: upcomingPage,
          limit: CHEQUE_PAGE_SIZE,
          project: chequeProjectFilter || undefined,
          showAll: upcomingShowAll,
        }),
      ]);

      setMissedCheques(missedResponse.data);
      setMissedTotal(missedResponse.pagination.total);
      setMissedTotalPages(missedResponse.pagination.totalPages);

      setUpcomingCheques(upcomingResponse.data);
      setUpcomingTotal(upcomingResponse.pagination.total);
      setUpcomingTotalPages(upcomingResponse.pagination.totalPages);
    } catch (error) {
      console.error("Error fetching cheque tracking data:", error);
    }
  };

  // Re-fetch cheque data when the modal is open and pagination/filters change
  useEffect(() => {
    if (showChequeTrackingModal) {
      fetchChequeTrackingData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showChequeTrackingModal,
    missedPage,
    upcomingPage,
    chequeProjectFilter,
    upcomingShowAll,
  ]);

  // Mark a cheque as received (records chequeReceivedDate) on the missed tab
  const markReceived = async (incomeId: number, receivedDate: string) => {
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(receivedDate) || isNaN(new Date(receivedDate).getTime())) {
      alert("Please select a valid received date");
      return;
    }
    try {
      setMarkingReceived((prev) => ({ ...prev, [incomeId]: true }));
      await incomeService.updateIncome(incomeId, {
        chequeReceivedDate: receivedDate,
      });
      setReceivedDates((prev) => ({ ...prev, [incomeId]: "" }));
      await fetchChequeTrackingData();
      await fetchIncomes();
    } catch (error) {
      console.error("Error marking cheque received:", error);
      alert("Failed to mark received. Please try again.");
    } finally {
      setMarkingReceived((prev) => ({ ...prev, [incomeId]: false }));
    }
  };

  const updateDepositDate = async (incomeId: number, depositDate: string) => {
    // Validate date format (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(depositDate)) {
      alert("Please enter a valid date in YYYY-MM-DD format");
      return;
    }

    // Validate that it's a valid date
    const dateObj = new Date(depositDate);
    if (isNaN(dateObj.getTime())) {
      alert("Please enter a valid date");
      return;
    }

    try {
      setUpdatingDeposit((prev) => ({ ...prev, [incomeId]: true }));

      await incomeService.updateIncome(incomeId, {
        chequeDepositDate: depositDate,
      });

      // Clear the input field for this income
      setDepositDates((prev) => ({ ...prev, [incomeId]: "" }));

      // Refresh the cheque tracking data
      await fetchChequeTrackingData();

      // Also refresh the main income list
      await fetchIncomes();

      alert("Deposit date updated successfully!");
    } catch (error) {
      console.error("Error updating deposit date:", error);
      alert("Failed to update deposit date. Please try again.");
    } finally {
      setUpdatingDeposit((prev) => ({ ...prev, [incomeId]: false }));
    }
  };

  const resetForm = () => {
    setFormData({
      bookingId: "",
      amount: "",
      paymentDate: "",
      paymentMethod: "",
      referenceNumber: "",
      bankName: "",
      description: "",
      receiptName: "",
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const data = {
        bookingId: parseInt(formData.bookingId),
        amount: parseFloat(formData.amount),
        paymentDate: formData.paymentDate,
        paymentMethod: formData.paymentMethod as any,
        referenceNumber: formData.referenceNumber || undefined,
        bankName: formData.bankName || undefined,
        description: formData.description || undefined,
        receiptName: formData.receiptName || undefined,
      };

      if (editingIncome) {
        await incomeService.updateIncome(editingIncome.id, data);
      } else {
        await incomeService.createIncome(data);
      }

      setShowModal(false);
      setEditingIncome(null);
      resetForm();
      fetchIncomes();
    } catch (error: any) {
      console.error("Error saving income:", error);
      
      // Check if this is a duplicate entry error
      if (error.response?.data?.error === 'DUPLICATE_ENTRY') {
        const message = error.response.data.message || 'Entry Already Exists';
        alert(message);
      } else {
        alert("Failed to save income. Please try again.");
      }
    }
  };

  const handleEdit = (income: Income) => {
    // Redirect to AddIncome page in edit mode
    navigate(`/ledger/add-income?id=${income.id}`);
  };

  const handleDelete = (income: Income) => {
    setIncomeToDelete(income);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (incomeToDelete) {
      try {
        await incomeService.deleteIncome(incomeToDelete.id);
        setShowDeleteDialog(false);
        setIncomeToDelete(null);
        fetchIncomes();
      } catch (error: any) {
        console.error("Error deleting income:", error);
        const errorMessage = error.response?.data?.error || "Failed to delete income";
        alert(errorMessage);
        setShowDeleteDialog(false);
        setIncomeToDelete(null);
      }
    }
  };

  const handleViewHistory = async (income: Income) => {
    try {
      const incomes = await incomeService.getIncomesByBooking(income.bookingId);
      setSelectedBookingIncomes(incomes);
      setShowHistoryModal(true);
    } catch (error) {
      console.error("Error fetching income history:", error);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleStatusFilterChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handlePaymentMethodFilterChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setPaymentMethodFilter(e.target.value);
    setCurrentPage(1);
  };

  if (loading && incomes.length === 0) {
    return <div className="loading">Loading incomes...</div>;
  }

  return (
    <div className="page-container income-page">
      {/* Action Bar */}
      <CompactActionBar>
        <div style={{ display: "flex", gap: "12px", alignItems: "center", flex: 1 }}>
          <button
            className={`filter-toggle-btn ${showFilters ? "active" : ""}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter /> Filters
          </button>

          <div className="cab-filter-group">
            <span>Project:</span>
            <select
              className="cab-select"
              value={filterProject}
              onChange={(e) => {
                setFilterProject(e.target.value);
                setAppliedFilters((prev) => ({ ...prev, project: e.target.value }));
                setCurrentPage(1);
              }}
            >
              <option value="">All Projects</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>

          <div className="cab-filter-group">
            <span>Status:</span>
            <select
              className="cab-select"
              value={statusFilter}
              onChange={handleStatusFilterChange}
            >
              <option value="">All Status</option>
              {incomeService.getStatusOptions().map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>

          <div className="cab-filter-group">
            <span>Payment:</span>
            <select
              className="cab-select"
              value={paymentMethodFilter}
              onChange={handlePaymentMethodFilterChange}
            >
              <option value="">All Methods</option>
              {incomeService.getPaymentMethods().map((method) => (
                <option key={method.value} value={method.value}>
                  {method.label}
                </option>
              ))}
            </select>
          </div>

          <div className="cab-filter-group">
            <span>Show:</span>
            <select
              className="cab-select"
              style={{ minWidth: "80px" }}
              value={limit}
              onChange={(e) => {
                setLimit(parseInt(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={300}>300</option>
              <option value={400}>400</option>
              <option value={500}>500</option>
            </select>
          </div>

          <button
            className="btn btn-secondary"
            onClick={() => {
              setMissedPage(1);
              setUpcomingPage(1);
              setShowChequeTrackingModal(true);
            }}
            style={{ marginLeft: 'auto', padding: '6px 12px', fontSize: '13px' }}
          >
            <FaEye /> Cheque Tracking
          </button>
          
          <PermissionGuard permission="income.create">
            <button
              className="btn btn-primary"
              onClick={() => navigate("/ledger/add-income")}
              style={{ padding: '6px 12px', fontSize: '13px' }}
            >
              <FaPlus /> Add Income
            </button>
          </PermissionGuard>
        </div>
      </CompactActionBar>

      {/* Filters Panel */}
      {showFilters && (
        <SharedFilter
          projects={projects}
          unitNumber={filterUnit}
          onUnitNumberChange={setFilterUnit}
          customerName={filterCustomer}
          onCustomerNameChange={setFilterCustomer}
          mobile={filterMobile}
          onMobileChange={setFilterMobile}
          dateFrom={filterDateFrom}
          onDateFromChange={setFilterDateFrom}
          dateTo={filterDateTo}
          onDateToChange={setFilterDateTo}
          dateLabel="Receipt Date"
          onApply={() => {
            handleApplyFilters();
            setCurrentPage(1);
          }}
          onClear={clearAllFilters}
        />
      )}

      <div className="content-section">
        <table className="standard-table slim">
          <thead>
            <tr>
              <th>Sr. No</th>
              <th>Customer</th>
              <th>Project</th>
              <th>Unit</th>
              <th>Amount</th>
              <th>Receipt No</th>
              <th>Received Date</th>
              <th>Status</th>
              <th>Payment Method</th>
              <th>Bank Name</th>
              <th>Reference</th>
              <th>Remarks</th>
              <th>Instrument Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {incomes.map((income, index) => (
              <React.Fragment key={income.id}>
              <tr key={income.id} className={`${income.amount < 0 ? 'return-income' : ''} ${income.status === 'rejected' ? 'rejected-row' : ''}`.trim()}>
                <td style={{ fontWeight: "600", color: "#4f46e5" }}>
                  {income.amount < 0 && income.returnAllocations && income.returnAllocations.length > 0 ? (
                    <button
                      className="expand-btn"
                      onClick={() => toggleExpandReturn(income.id)}
                      style={{ marginRight: 6 }}
                    >
                      {expandedReturnIds.has(income.id) ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}
                    </button>
                  ) : income.amount < 0 ? (
                    <span style={{ width: 22, display: 'inline-block' }} />
                  ) : null}
                  {incomes.length - index}
                </td>
                <td>
                  <div className="customer-info" style={{ maxWidth: '160px', whiteSpace: 'normal', lineHeight: '1.3' }}>
                    {incomeService.getCustomerName(income)}
                  </div>
                </td>
                <td>
                  <span className="project-name">
                    {income.booking?.project?.name || "N/A"}
                  </span>
                </td>
                <td>
                  <span className="unit-number">
                    {incomeService.getUnitDisplay(income)}
                  </span>
                </td>
                <td className="amount-cell" style={{ textAlign: 'right' }}>
                  <span className="amount" style={{color: income.amount < 0 ? '#dc2626' : '#059669'}}>
                    {incomeService.formatAmount(income.amount)}
                  </span>
                </td>
                <td>
                  <span className="receipt-no" style={{ fontSize: '12px' }}>
                    {income.receiptNo || '-'}
                  </span>
                </td>
                <td>
                  <span className="payment-date">
                    {incomeService.formatDate(income.chequeReceivedDate || income.paymentDate)}
                  </span>
                </td>
                <td>
                  {income.status === 'approved' ? (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#dcfce7', color: '#166534', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 500 }}>
                      <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: '#16a34a', display: 'inline-block' }} />
                      {income.approvedAt ? incomeService.formatDate(income.approvedAt) : 'Approved'}
                    </div>
                  ) : income.status === 'rejected' ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', maxWidth: '180px' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#fef2f2', color: '#991b1b', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 500, alignSelf: 'flex-start' }}>
                        <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: '#dc2626', display: 'inline-block' }} />
                        Rejected
                      </div>
                      {income.rejectionReason && (
                        <span style={{ fontSize: '11px', color: '#dc2626', lineHeight: 1.3 }} title={income.rejectionReason}>
                          {income.rejectionReason}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#fef2f2', color: '#991b1b', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 500 }}>
                      <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: '#dc2626', display: 'inline-block' }} />
                      Pending
                    </div>
                  )}
                </td>
                <td>
                  <span className="payment-method">
                    {incomeService.getPaymentMethodDisplay(
                      income.paymentMethod
                    )}
                  </span>
                </td>
                <td>
                  <span className="bank-name">{income.bankName || "-"}</span>
                </td>
                <td>
                  <span className="reference">
                    {income.referenceNumber || "-"}
                  </span>
                </td>
                <td>
                  <span className="remarks" style={{ fontSize: "12px", color: "#6b7280" }}>
                    {income.remarks || "-"}
                  </span>
                </td>
                <td>
                  <span className="instrument-date">
                    {income.instrumentDate
                      ? incomeService.formatDate(income.instrumentDate)
                      : "-"}
                  </span>
                </td>
                <td>
                  <div className="income-actions">
                    <button
                      className="btn-icon view"
                      onClick={() => handleViewHistory(income)}
                      title="View Payment History"
                    >
                      <FaEye />
                    </button>
                    {(income.status === "approval_pending" || income.status === "rejected" || user?.isSuperAdmin) && (
                      <>
                        <PermissionGuard permission="income.edit">
                          <button
                            className="btn-icon edit"
                            onClick={() => handleEdit(income)}
                            title={income.status === 'rejected' ? `Edit & Resubmit (Rejected: ${income.rejectionReason || 'no reason'})` : 'Edit Income'}
                          >
                            <FaEdit />
                          </button>
                        </PermissionGuard>
                        {(income.status === "approval_pending" || user?.isSuperAdmin) && (
                          <PermissionGuard permission="income.delete">
                            <button
                              className="btn-icon delete"
                              onClick={() => handleDelete(income)}
                              title="Delete Income"
                            >
                              <FaTrash />
                            </button>
                          </PermissionGuard>
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>

              {/* Expanded return allocation row */}
              {income.amount < 0 && expandedReturnIds.has(income.id) && income.returnAllocations && income.returnAllocations.length > 0 && (
                <tr className="allocation-detail-row">
                  <td colSpan={14}>
                    <div className="allocation-details">
                      <h4>Return Allocations</h4>
                      <table className="allocation-table">
                        <thead>
                          <tr>
                            <th>Source Income</th>
                            <th>Date</th>
                            <th>Allocated</th>
                            <th>Reference</th>
                          </tr>
                        </thead>
                        <tbody>
                          {income.returnAllocations.map(alloc => (
                            <tr key={alloc.id}>
                              <td>₹{(alloc.sourceIncome.amount || 0).toLocaleString()}</td>
                              <td>{alloc.sourceIncome.paymentDate ? incomeService.formatDate(alloc.sourceIncome.paymentDate) : '-'}</td>
                              <td style={{ color: '#dc2626' }}>-₹{alloc.allocatedAmount.toLocaleString()}</td>
                              <td>{alloc.sourceIncome.referenceNumber || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {incomes.length === 0 && !loading && (
        <div className="empty-state">
          <FaMoneyBillWave className="empty-icon" />
          <h3>No Income Records Found</h3>
          <p>Start by adding your first income entry</p>
          <PermissionGuard permission="income.create">
            <button
              className="add-income-btn"
              onClick={() => setShowModal(true)}
            >
              <FaPlus /> Add Income
            </button>
          </PermissionGuard>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="page-btn"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            Previous
          </button>

          <div className="page-numbers">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const startPage = Math.max(1, currentPage - 2);
              const page = Math.min(startPage + i, totalPages);

              if (page > totalPages) return null;

              return (
                <button
                  key={page}
                  className={`page-btn ${currentPage === page ? "active" : ""}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              );
            }).filter(Boolean)}
          </div>

          <button
            className="page-btn"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            Next
          </button>
        </div>
      )}

      {/* Add/Edit Income Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingIncome ? "Edit Income" : "Add New Income"}</h2>
              <button
                className="close-btn"
                onClick={() => {
                  setShowModal(false);
                  setEditingIncome(null);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label htmlFor="receiptName">Receipt Name</label>
                <input
                  type="text"
                  id="receiptName"
                  name="receiptName"
                  value={formData.receiptName}
                  onChange={handleInputChange}
                  placeholder="Name of person giving payment (if different from customer)"
                />
              </div>

              <div className="form-group">
                <label htmlFor="bookingId">Booking ID *</label>
                <input
                  type="number"
                  id="bookingId"
                  name="bookingId"
                  value={formData.bookingId}
                  onChange={handleInputChange}
                  onWheel={(e) => e.currentTarget.blur()} // Prevent scroll wheel changes
                  placeholder="Enter booking ID"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="amount">Amount *</label>
                <input
                  type="number"
                  id="amount"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  onWheel={(e) => e.currentTarget.blur()} // Prevent scroll wheel changes
                  placeholder="Enter amount (use negative for refunds)"
                  step="0.01"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="paymentDate">Payment Date *</label>
                <input
                  type="date"
                  id="paymentDate"
                  name="paymentDate"
                  value={formData.paymentDate}
                  onChange={handleInputChange}
                  max={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="paymentMethod">Payment Method *</label>
                <select
                  id="paymentMethod"
                  name="paymentMethod"
                  value={formData.paymentMethod}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Payment Method</option>
                  {incomeService.getPaymentMethods().map((method) => (
                    <option key={method.value} value={method.value}>
                      {method.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="referenceNumber">Reference Number</label>
                <input
                  type="text"
                  id="referenceNumber"
                  name="referenceNumber"
                  value={formData.referenceNumber}
                  onChange={handleInputChange}
                  placeholder="Cheque number, transaction ID, etc."
                />
              </div>

              <div className="form-group">
                <label htmlFor="bankName">Bank Name</label>
                <input
                  type="text"
                  id="bankName"
                  name="bankName"
                  value={formData.bankName}
                  onChange={handleInputChange}
                  placeholder="Bank name (for electronic transfers)"
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Additional notes (optional)"
                  rows={3}
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => {
                    setShowModal(false);
                    setEditingIncome(null);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="submit-btn">
                  {editingIncome ? "Update Income" : "Add Income"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment History Modal */}
      {showHistoryModal && (
        <div className="modal-overlay">
          <div className="modal large">
            <div className="modal-header">
              <h2>Payment History</h2>
              <button
                className="close-btn"
                onClick={() => setShowHistoryModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-content">
              <div className="content-section">
                <table className="standard-table">
                  <thead>
                    <tr>
                      <th>Receipt Name</th>
                      <th>Amount</th>
                      <th>Received Date</th>
                      <th>Method</th>
                      <th>Bank Name</th>
                      <th>Reference</th>
                      <th>Instrument Date</th>
                      <th>Status</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedBookingIncomes.map((income) => (
                      <tr key={income.id}>
                        <td>{income.receiptName || "-"}</td>
                        <td style={{color: income.amount < 0 ? '#dc2626' : '#059669'}}>{incomeService.formatAmount(income.amount)}</td>
                        <td>{incomeService.formatDate(income.chequeReceivedDate || income.paymentDate)}</td>
                        <td>
                          {incomeService.getPaymentMethodDisplay(
                            income.paymentMethod
                          )}
                        </td>
                        <td>{income.bankName || "-"}</td>
                        <td>{income.referenceNumber || "-"}</td>
                        <td>
                          {income.instrumentDate
                            ? incomeService.formatDate(income.instrumentDate)
                            : "-"}
                        </td>
                        <td>
                          <span
                            className={`status ${
                              incomeService.getStatusDisplay(income.status)
                                .className
                            }`}
                          >
                            {incomeService.getStatusDisplay(income.status).text}
                          </span>
                        </td>
                        <td>{incomeService.formatDate(income.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Delete Income"
        message={`Are you sure you want to delete this income entry of ${
          incomeToDelete
            ? incomeService.formatAmount(incomeToDelete.amount)
            : ""
        }? This action cannot be undone.`}
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteDialog(false);
          setIncomeToDelete(null);
        }}
      />

      {/* Cheque Tracking Modal */}
      {showChequeTrackingModal && (
        <div className="modal-overlay">
          <div className="modal-content large-modal">
            <div className="modal-header">
              <h3>Cheque Deposit Tracking</h3>
              <button
                onClick={() => setShowChequeTrackingModal(false)}
                className="close-btn"
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              {/* Filter bar: project (superadmin/multi-project) + show-all (upcoming) */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  flexWrap: "wrap",
                  marginBottom: "12px",
                }}
              >
                <div className="cab-filter-group">
                  <span>Project:</span>
                  <select
                    className="cab-select"
                    value={chequeProjectFilter}
                    onChange={(e) => {
                      setChequeProjectFilter(e.target.value);
                      setMissedPage(1);
                      setUpcomingPage(1);
                    }}
                  >
                    <option value="">All My Projects</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
                {activeTab === "upcoming" && (
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      fontSize: "13px",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={upcomingShowAll}
                      onChange={(e) => {
                        setUpcomingShowAll(e.target.checked);
                        setUpcomingPage(1);
                      }}
                    />
                    Show all upcoming (not just today &amp; tomorrow)
                  </label>
                )}
              </div>

              {/* Tab Navigation */}
              <div className="tab-navigation">
                <button
                  className={`tab-btn ${
                    activeTab === "missed" ? "active" : ""
                  }`}
                  onClick={() => setActiveTab("missed")}
                >
                  Missed Deposits ({missedTotal})
                </button>
                <button
                  className={`tab-btn ${
                    activeTab === "upcoming" ? "active" : ""
                  }`}
                  onClick={() => setActiveTab("upcoming")}
                >
                  Upcoming Deposits ({upcomingTotal})
                </button>
              </div>

              {/* Tab Content */}
              <div className="tab-content">
                {activeTab === "missed" && (
                  <div className="cheque-list">
                    <h4>Cheques with Missed Deposit Dates</h4>
                    <p className="tab-description">
                      Cheque payments whose instrument date is before today (
                      {new Date().toLocaleDateString()}) with no deposit date
                      recorded yet.
                    </p>
                    {missedCheques.length === 0 ? (
                      <p className="no-data">No missed deposits found!</p>
                    ) : (
                      <table className="cheque-table">
                        <thead>
                          <tr>
                            <th>Project</th>
                            <th>Customer</th>
                            <th>Amount</th>
                            <th>Reference</th>
                            <th>Instrument Date</th>
                            <th>Days Overdue</th>
                            <th>Record Deposit</th>
                            <th>Mark Received</th>
                          </tr>
                        </thead>
                        <tbody>
                          {missedCheques.map((cheque) => {
                            const daysOverdue = Math.floor(
                              (new Date().getTime() -
                                new Date(cheque.instrumentDate!).getTime()) /
                                (1000 * 60 * 60 * 24)
                            );
                            return (
                              <tr key={cheque.id}>
                                <td>{cheque.booking?.project?.name || "-"}</td>
                                <td>
                                  {incomeService.getCustomerName(cheque)}
                                </td>
                                <td>
                                  {incomeService.formatAmount(cheque.amount)}
                                </td>
                                <td>{cheque.referenceNumber || "-"}</td>
                                <td>
                                  {incomeService.formatDate(
                                    cheque.instrumentDate!
                                  )}
                                </td>
                                <td className="overdue-days">
                                  {daysOverdue} days
                                </td>
                                <td>
                                  <div className="deposit-date-action">
                                    <input
                                      type="date"
                                      value={depositDates[cheque.id] || ""}
                                      onChange={(e) => {
                                        setDepositDates((prev) => ({
                                          ...prev,
                                          [cheque.id]: e.target.value,
                                        }));
                                      }}
                                      max={new Date().toISOString().split("T")[0]}
                                      className="deposit-date-input"
                                    />
                                    <button
                                      onClick={() => {
                                        const date = depositDates[cheque.id];
                                        if (date) {
                                          updateDepositDate(cheque.id, date);
                                        } else {
                                          alert("Please select a date first");
                                        }
                                      }}
                                      disabled={
                                        !depositDates[cheque.id] ||
                                        updatingDeposit[cheque.id]
                                      }
                                      className="apply-btn"
                                    >
                                      {updatingDeposit[cheque.id]
                                        ? "Saving..."
                                        : "Deposit"}
                                    </button>
                                  </div>
                                </td>
                                <td>
                                  <div className="deposit-date-action">
                                    <input
                                      type="date"
                                      value={receivedDates[cheque.id] || ""}
                                      onChange={(e) => {
                                        setReceivedDates((prev) => ({
                                          ...prev,
                                          [cheque.id]: e.target.value,
                                        }));
                                      }}
                                      max={new Date().toISOString().split("T")[0]}
                                      className="deposit-date-input"
                                    />
                                    <button
                                      onClick={() => {
                                        const date = receivedDates[cheque.id];
                                        if (date) {
                                          markReceived(cheque.id, date);
                                        } else {
                                          alert("Please select a date first");
                                        }
                                      }}
                                      disabled={
                                        !receivedDates[cheque.id] ||
                                        markingReceived[cheque.id]
                                      }
                                      className="apply-btn"
                                    >
                                      {markingReceived[cheque.id]
                                        ? "Saving..."
                                        : "Received"}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                    {missedTotalPages > 1 && (
                      <div className="cheque-pagination">
                        <button
                          disabled={missedPage <= 1}
                          onClick={() => setMissedPage((p) => p - 1)}
                        >
                          Previous
                        </button>
                        <span>
                          Page {missedPage} of {missedTotalPages} ({missedTotal}{" "}
                          total)
                        </span>
                        <button
                          disabled={missedPage >= missedTotalPages}
                          onClick={() => setMissedPage((p) => p + 1)}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === "upcoming" && (
                  <div className="cheque-list">
                    <h4>Upcoming Cheque Deposits</h4>
                    <p className="tab-description">
                      {upcomingShowAll
                        ? "All cheques dated today or later, awaiting deposit."
                        : "Cheques to be deposited today & tomorrow. Tick “Show all” above to see everything ahead."}
                    </p>
                    {upcomingCheques.length === 0 ? (
                      <p className="no-data">No upcoming deposits found!</p>
                    ) : (
                      <table className="cheque-table">
                        <thead>
                          <tr>
                            <th>Project</th>
                            <th>Customer</th>
                            <th>Amount</th>
                            <th>Reference</th>
                            <th>Instrument Date</th>
                            <th>Days Until Due</th>
                            <th>Record Deposit</th>
                          </tr>
                        </thead>
                        <tbody>
                          {upcomingCheques.map((cheque) => {
                            const daysUntilDue = Math.floor(
                              (new Date(cheque.instrumentDate!).getTime() -
                                new Date().getTime()) /
                                (1000 * 60 * 60 * 24)
                            );
                            return (
                              <tr key={cheque.id}>
                                <td>{cheque.booking?.project?.name || "-"}</td>
                                <td>
                                  {incomeService.getCustomerName(cheque)}
                                </td>
                                <td>
                                  {incomeService.formatAmount(cheque.amount)}
                                </td>
                                <td>{cheque.referenceNumber || "-"}</td>
                                <td>
                                  {incomeService.formatDate(
                                    cheque.instrumentDate!
                                  )}
                                </td>
                                <td className="days-until-due">
                                  {daysUntilDue} days
                                </td>
                                <td>
                                  <div className="deposit-date-action">
                                    <input
                                      type="date"
                                      value={depositDates[cheque.id] || ""}
                                      onChange={(e) => {
                                        setDepositDates((prev) => ({
                                          ...prev,
                                          [cheque.id]: e.target.value,
                                        }));
                                      }}
                                      max={new Date().toISOString().split("T")[0]}
                                      className="deposit-date-input"
                                    />
                                    <button
                                      onClick={() => {
                                        const date = depositDates[cheque.id];
                                        if (date) {
                                          updateDepositDate(cheque.id, date);
                                        } else {
                                          alert("Please select a date first");
                                        }
                                      }}
                                      disabled={
                                        !depositDates[cheque.id] ||
                                        updatingDeposit[cheque.id]
                                      }
                                      className="apply-btn"
                                    >
                                      {updatingDeposit[cheque.id]
                                        ? "Saving..."
                                        : "Deposit"}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                    {upcomingTotalPages > 1 && (
                      <div className="cheque-pagination">
                        <button
                          disabled={upcomingPage <= 1}
                          onClick={() => setUpcomingPage((p) => p - 1)}
                        >
                          Previous
                        </button>
                        <span>
                          Page {upcomingPage} of {upcomingTotalPages} (
                          {upcomingTotal} total)
                        </span>
                        <button
                          disabled={upcomingPage >= upcomingTotalPages}
                          onClick={() => setUpcomingPage((p) => p + 1)}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncomePage;
